import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface LexChatMessage {
  sender: 'user' | 'lex';
  text: string;
  timestamp: string;
}

export interface LexSessionContext {
  [key: string]: unknown;
}

interface LexChatStoreState {
  isLexOpen: boolean;
  isLexPinned: boolean;
  isSidebarOpen: boolean;
  isSidebarPinned: boolean;
  lexChatHistory: LexChatMessage[];
  lexSessionContext: LexSessionContext;
  setLexOpen: (open: boolean) => void;
  setLexPinned: (pinned: boolean) => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarPinned: (pinned: boolean) => void;
  addLexMessage: (msg: LexChatMessage) => void;
  clearLexHistory: () => void;
  updateLexSessionContext: (context: Partial<LexSessionContext>) => void;
  clearLexSessionContext: () => void;
}

export const useLexChatStore = create<LexChatStoreState>()(
  persist(
    (set, get) => {
      void get;
      return {
        isLexOpen: false,
        isLexPinned: false,
        isSidebarOpen: false,
        isSidebarPinned: false,
        lexChatHistory: [],
        lexSessionContext: {},
        setLexOpen: (open: boolean) => set((state: LexChatStoreState) => ({
          isLexOpen: open,
          // Mutual exclusion: if both are not pinned, only one can be open
          isSidebarOpen: open && !state.isSidebarPinned ? false : state.isSidebarOpen
        })),
        setLexPinned: (pinned: boolean) => set((state: LexChatStoreState) => ({
          isLexPinned: pinned,
          // Pinning implies visible/open panel to avoid unrecoverable UI states.
          isLexOpen: pinned ? true : state.isLexOpen
        })),
        setSidebarOpen: (open: boolean) => set((state: LexChatStoreState) => ({
          isSidebarOpen: open,
          isLexOpen: open && !state.isLexPinned ? false : state.isLexOpen
        })),
        setSidebarPinned: (pinned: boolean) => set({ isSidebarPinned: pinned }),
        addLexMessage: (msg: LexChatMessage) => set((state: LexChatStoreState) => ({
          lexChatHistory: [...state.lexChatHistory, msg]
        })),
        clearLexHistory: () => set({ lexChatHistory: [] }),
        updateLexSessionContext: (context: Partial<LexSessionContext>) => set((state: LexChatStoreState) => ({
          lexSessionContext: { ...state.lexSessionContext, ...context }
        })),
        clearLexSessionContext: () => set({ lexSessionContext: {} }),
      };
    },
    {
      name: 'lex-chat-store-matters',
      partialize: (state: LexChatStoreState) => ({
        isLexOpen: state.isLexOpen,
        isLexPinned: state.isLexPinned,
        isSidebarPinned: state.isSidebarPinned,
        lexChatHistory: state.lexChatHistory,
        lexSessionContext: state.lexSessionContext,
      }),
    }
  )
); 
